import * as React from "react";
import { Loader2, UploadCloud } from "lucide-react";
import { Button } from "@/v2/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/v2/components/ui/dialog";
import { Input } from "@/v2/components/ui/input";
import { uploadErrorMessage } from "../uploadsApi";
import { BARE_CONTROL, Field } from "./parts";

export const ACCEPTED_VIDEO = ".mp4,.mov,video/mp4,video/quicktime";

/** "promo_final-v2.mp4" -> "promo final v2": a starting title the admin edits. */
export function titleFromFileName(name: string): string {
  return name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Starts the upload; resolves once the session is open (the bytes then
   *  keep streaming in the background after the dialog closes). */
  onStart: (file: File, title: string, description: string | null) => Promise<void>;
}

/**
 * Upload a video to the Other Content library (HTPTV2-89): file, a required
 * title and an optional description. The server enforces the size cap and
 * the formats; its message is shown here verbatim when it refuses.
 */
export function UploadDialog({ open, onOpenChange, onStart }: Props) {
  const [file, setFile] = React.useState<File | null>(null);
  const [title, setTitle] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setFile(null);
      setTitle("");
      setDescription("");
      setError(null);
      setBusy(false);
    }
  }, [open]);

  const pick = (next: File | null) => {
    setFile(next);
    setError(null);
    if (next && !title.trim()) setTitle(titleFromFileName(next.name));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return setError("Choose a video file.");
    if (!title.trim()) return setError("Title is required.");
    setBusy(true);
    setError(null);
    try {
      await onStart(file, title.trim(), description.trim() || null);
      onOpenChange(false);
    } catch (err) {
      setError(uploadErrorMessage(err, "Could not start the upload."));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload video</DialogTitle>
          <DialogDescription>
            MP4 or MOV. After upload it is transcoded, then it can be dragged onto any channel.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col" style={{ gap: 16 }}>
          <label
            className="flex cursor-pointer flex-col items-center justify-center rounded-md border border-dashed border-border bg-muted text-center"
            style={{ padding: 20, gap: 6 }}
          >
            <UploadCloud size={22} className="text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">
              {file ? file.name : "Choose a video file"}
            </span>
            {file ? (
              <span className="text-xs text-muted-foreground">
                {(file.size / (1024 * 1024)).toFixed(1)} MB
              </span>
            ) : null}
            <input
              type="file"
              accept={ACCEPTED_VIDEO}
              aria-label="Video file"
              className="sr-only"
              onChange={(e) => pick(e.target.files?.[0] ?? null)}
            />
          </label>

          <div>
            <div className="text-sm text-foreground" style={{ marginBottom: 6 }}>
              Title
            </div>
            <Field>
              <Input
                aria-label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Shown on the timeline and in the guide"
                className={BARE_CONTROL}
              />
            </Field>
          </div>

          <div>
            <div className="text-sm text-foreground" style={{ marginBottom: 6 }}>
              Description
            </div>
            <Field className="items-stretch">
              <textarea
                aria-label="Description"
                value={description}
                rows={3}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional, shown under the title in the guide"
                className="sched-bare h-auto w-full resize-none border-0 bg-transparent text-sm shadow-none ring-0 focus-visible:ring-0"
                style={{ padding: "10px 12px", lineHeight: 1.5 }}
              />
            </Field>
          </div>

          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}

          <div className="flex justify-end" style={{ gap: 8 }}>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy} className="bg-action text-white hover:bg-action-hover">
              {busy ? <Loader2 size={16} className="animate-spin" /> : null}
              Upload
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
